/**
 * PWA support of the staff web app: service worker registration and updates,
 * the install prompt (Chrome/Android `beforeinstallprompt`, iOS instructions)
 * and web push subscription. Pure helpers take their environment as arguments
 * so they can be tested without a browser.
 */
import { useSyncExternalStore } from 'react'

export type Platform = 'ios' | 'android' | 'desktop'

export interface Env {
  userAgent: string
  maxTouchPoints?: number
  standalone?: boolean
  hasServiceWorker?: boolean
  hasPushManager?: boolean
  hasNotification?: boolean
}

export function envOf(w: Window & typeof globalThis = window): Env {
  const nav = w.navigator as Navigator & { standalone?: boolean }
  return {
    userAgent: nav.userAgent,
    maxTouchPoints: nav.maxTouchPoints,
    standalone: isStandalone(w),
    hasServiceWorker: 'serviceWorker' in nav,
    hasPushManager: 'PushManager' in w,
    hasNotification: 'Notification' in w,
  }
}

/** iPadOS 13+ reports a desktop Safari UA; touch points give it away. */
export function detectPlatform(e: Pick<Env, 'userAgent' | 'maxTouchPoints'>): Platform {
  const ua = e.userAgent
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (e.maxTouchPoints ?? 0) > 1)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

/** iOS major.minor as a number (16.4 → 16.4), null when not iOS or unknown. */
export function iosVersion(ua: string): number | null {
  const m = /OS (\d+)[_.](\d+)/.exec(ua) ?? /Version\/(\d+)\.(\d+).*Safari/.exec(ua)
  if (!m) return null
  return Number(m[1]) + Number(m[2]) / 10
}

export function isStandalone(w: Window = window): boolean {
  try {
    if (w.matchMedia?.('(display-mode: standalone)').matches || w.matchMedia?.('(display-mode: window-controls-overlay)').matches) return true
  } catch {
    /* ignore */
  }
  return (w.navigator as Navigator & { standalone?: boolean }).standalone === true
}

/**
 * Whether web push can be enabled here. iOS delivers web push only to an app
 * added to the Home Screen (iOS 16.4+), never to a Safari tab.
 */
export type PushSupport = 'supported' | 'ios-install-first' | 'ios-too-old' | 'unsupported'

export function pushSupport(e: Env): PushSupport {
  if (detectPlatform(e) === 'ios') {
    const v = iosVersion(e.userAgent)
    if (v !== null && v < 16.4) return 'ios-too-old'
    if (!e.standalone) return 'ios-install-first'
  }
  return e.hasServiceWorker && e.hasPushManager && e.hasNotification ? 'supported' : 'unsupported'
}

// ---- install prompt ----------------------------------------------------------

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallMode = 'installed' | 'prompt' | 'ios' | 'none'

/** Which install affordance to show. */
export function installMode(e: Env, hasPrompt: boolean): InstallMode {
  if (e.standalone) return 'installed'
  if (hasPrompt) return 'prompt'
  if (detectPlatform(e) === 'ios') return 'ios'
  return 'none'
}

// ---- shared state (useSyncExternalStore) ----------------------------------------

export interface PwaState {
  installEvent: BeforeInstallPromptEvent | null
  installed: boolean
  updateReady: boolean
}

let state: PwaState = { installEvent: null, installed: false, updateReady: false }
const listeners = new Set<() => void>()

function setState(patch: Partial<PwaState>) {
  state = { ...state, ...patch }
  listeners.forEach((f) => f())
}

export function getPwaState(): PwaState {
  return state
}

export function resetPwaState() {
  state = { installEvent: null, installed: false, updateReady: false }
  waiting = null
  listeners.forEach((f) => f())
}

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (f) => {
      listeners.add(f)
      return () => listeners.delete(f)
    },
    () => state,
    () => state,
  )
}

/** Keeps Chrome's install prompt for our own «Встановити застосунок» button. */
export function captureInstallPrompt(w: Window = window): () => void {
  const onPrompt = (ev: Event) => {
    ev.preventDefault()
    setState({ installEvent: ev as BeforeInstallPromptEvent })
  }
  const onInstalled = () => setState({ installEvent: null, installed: true })
  w.addEventListener('beforeinstallprompt', onPrompt)
  w.addEventListener('appinstalled', onInstalled)
  return () => {
    w.removeEventListener('beforeinstallprompt', onPrompt)
    w.removeEventListener('appinstalled', onInstalled)
  }
}

/** Shows the browser's install dialog (only from a click). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const ev = state.installEvent
  if (!ev) return 'unavailable'
  await ev.prompt()
  const { outcome } = await ev.userChoice
  setState({ installEvent: null, installed: outcome === 'accepted' })
  return outcome
}

// ---- service worker and updates --------------------------------------------------

let waiting: ServiceWorker | null = null

/** Tracks a registration: a waiting worker means a new version is ready. */
export function watchRegistration(reg: ServiceWorkerRegistration) {
  const check = () => {
    if (reg.waiting && navigator.serviceWorker?.controller) {
      waiting = reg.waiting
      setState({ updateReady: true })
    }
  }
  check()
  reg.addEventListener('updatefound', () => {
    const sw = reg.installing
    sw?.addEventListener('statechange', () => {
      if (sw.state === 'installed') check()
    })
  })
}

/** «Оновити»: activate the waiting worker, then reload once it controls the page. */
export function applyUpdate(reload: () => void = () => window.location.reload()) {
  const sw = waiting
  if (!sw) {
    reload()
    return
  }
  let done = false
  navigator.serviceWorker?.addEventListener('controllerchange', () => {
    if (!done) {
      done = true
      reload()
    }
  })
  sw.postMessage({ type: 'SKIP_WAITING' })
  setState({ updateReady: false })
}

/** Registers /sw.js in production builds and checks for updates hourly. */
export async function registerServiceWorker(onNavigate?: (url: string) => void): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
    const d = ev.data as { type?: string; url?: string } | null
    if (d?.type === 'dovira:navigate' && typeof d.url === 'string' && d.url.startsWith('/') && !d.url.startsWith('//')) onNavigate?.(d.url)
  })
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
    watchRegistration(reg)
    window.setInterval(() => void reg.update().catch(() => undefined), 60 * 60 * 1000)
    return reg
  } catch {
    return null
  }
}

// ---- web push --------------------------------------------------------------------

export function urlBase64ToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4)
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false
  const x = new Uint8Array(a)
  return x.length === b.length && x.every((v, i) => v === b[i])
}

export async function sha256Hex(s: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s))
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export interface PushDeps {
  /** VAPID public key from webpush.config. */
  publicKey: string
  registration: () => Promise<ServiceWorkerRegistration>
  requestPermission: () => Promise<NotificationPermission>
  /** Stores the subscription on the server (webpush.subscribe). */
  save: (sub: PushSubscriptionJSON) => Promise<unknown>
}

export type EnableResult = 'enabled' | 'denied' | 'dismissed'

/**
 * Enables push on this browser. Must run from a click: the permission prompt is
 * never shown on page load. Re-uses the current subscription unless it was made
 * for another server key.
 */
export async function enablePush(d: PushDeps): Promise<EnableResult> {
  const perm = await d.requestPermission()
  if (perm === 'denied') return 'denied'
  if (perm !== 'granted') return 'dismissed'
  const reg = await d.registration()
  const key = urlBase64ToUint8Array(d.publicKey)
  let sub = await reg.pushManager.getSubscription()
  if (sub && !sameKey(sub.options?.applicationServerKey, key)) {
    await sub.unsubscribe().catch(() => false)
    sub = null
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  await d.save(sub.toJSON())
  return 'enabled'
}

/** Disables push on this browser; returns the endpoint that was removed. */
export async function disablePush(registration: () => Promise<ServiceWorkerRegistration>, remove: (endpoint: string) => Promise<unknown>): Promise<string | null> {
  const reg = await registration()
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return null
  const endpoint = sub.endpoint
  await sub.unsubscribe().catch(() => false)
  await remove(endpoint)
  return endpoint
}

/** The service worker registration, waiting at most `ms` for it to activate. */
export function readyRegistration(ms = 10_000): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) return Promise.reject(new Error('no service worker'))
  return Promise.race([
    navigator.serviceWorker.getRegistration('/').then((r) => r ?? navigator.serviceWorker.register('/sw.js', { scope: '/' })).then(() => navigator.serviceWorker.ready),
    new Promise<ServiceWorkerRegistration>((_, reject) => setTimeout(() => reject(new Error('service worker timeout')), ms)),
  ])
}

/** Endpoint hash of this browser's current subscription ('' when none). */
export async function currentEndpointHash(): Promise<string> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration('/')
    const sub = await reg?.pushManager.getSubscription()
    return sub ? await sha256Hex(sub.endpoint) : ''
  } catch {
    return ''
  }
}

/** Short device label for the device list («Chrome · Android»). */
export function deviceLabel(ua: string, maxTouchPoints = 0): string {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /SamsungBrowser/.test(ua)
      ? 'Samsung Internet'
      : /OPR\//.test(ua)
        ? 'Opera'
        : /Firefox|FxiOS/.test(ua)
          ? 'Firefox'
          : /Chrome|CriOS/.test(ua)
            ? 'Chrome'
            : /Safari/.test(ua)
              ? 'Safari'
              : 'Браузер'
  const p = detectPlatform({ userAgent: ua, maxTouchPoints })
  const os = p === 'ios' ? (/iPad/.test(ua) || /Macintosh/.test(ua) ? 'iPad' : 'iPhone') : p === 'android' ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} · ${os}` : browser
}
